import { useNavigate } from 'react-router-dom'

import { useMediaActionSummaryApi } from '@/common/api/modules/mediaAction'
import {
  ACTION_SUMMARY_COUNT_ITEMS,
  EXECUTABLE_NOW_ACTION_TYPES,
  buildActionTargetUrl,
  sourceAnalysisLabel
} from '@/common/app/modules/media/mediaAction'
import { mediaActionPath, mediaAnalysisResultPath } from '@/common/app/path'
import { MediaAction } from '@/common/model/media/mediaAction'

import AiButton from '@/components/view/atoms/button/AiButton'
import TextButton from '@/components/view/atoms/button/TextButton'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import PriorityBar from '@/components/view/molecules/bar/PriorityBar'

const isExecutableNow = (a: MediaAction): boolean =>
  (EXECUTABLE_NOW_ACTION_TYPES as readonly string[]).includes(a.action_type)

interface Props {
  siteId: number
}

// 次に進めるアクション（自動化確認のサマリー）
const MediaNextActionsPanel = ({ siteId }: Props) => {
  const navigate = useNavigate()
  const { data: actionSummary } = useMediaActionSummaryApi({ site_id: siteId })
  const counts = actionSummary?.counts ?? {}
  const top = actionSummary?.top ?? []

  return (
    <>
      <SummaryHeading
        subNode={
          <TextButton link={mediaActionPath(siteId)} nowrap arrow>
            推奨アクション管理画面
          </TextButton>
        }
      >
        次に進めるアクション
      </SummaryHeading>
      <Area bottomMargin={16}>
        <AreaContent>
          {/* 件数サマリー */}
          <SummaryItemList shadow>
            {ACTION_SUMMARY_COUNT_ITEMS.map((s) => (
              <SummaryItem key={s.key} color={'secondary'}>
                <ItemAlign>
                  <Text weight='bold'>{s.label}</Text>
                </ItemAlign>
                <ItemAlign>
                  <Text weight='bold' size={24}>
                    {counts[s.key] ?? 0}
                  </Text>
                </ItemAlign>
              </SummaryItem>
            ))}
          </SummaryItemList>
          {/* 上位3件抜粋 */}
          {top.length === 0 ? (
            <Text size={12} color={TextColor.SECONDARY} align='center'>
              未対応の推奨アクションはありません。各分析を実行すると候補が抽出されます。
            </Text>
          ) : (
            <div className='flex flex-col gap-2'>
              {top.slice(0, 3).map((a) => (
                <PriorityBar
                  key={a.id}
                  priority={a.priority}
                  title={a.title}
                  contentText={a.description}
                  subContent={
                    <div className='flex items-center gap-4'>
                      {a.source_analysis_type && a.source_history_id && (
                        <TextButton
                          newTab
                          link={mediaAnalysisResultPath(
                            siteId,
                            a.source_analysis_type,
                            a.source_history_id
                          )}
                          nowrap
                        >
                          {sourceAnalysisLabel(a.source_analysis_type)}
                        </TextButton>
                      )}
                      {isExecutableNow(a) && (
                        <AiButton
                          size='large'
                          onClick={(e) => {
                            e.stopPropagation()
                            const url = buildActionTargetUrl(a, siteId)
                            if (url) navigate(url)
                          }}
                          nowrap
                        >
                          AI新規作成
                        </AiButton>
                      )}
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </AreaContent>
      </Area>
    </>
  )
}

export default MediaNextActionsPanel
